import { useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { mergeProps, useViewportSize } from '@react-aria/utils';
import type { TrayProps } from '@react-types/overlays';
import type { DOMRef } from '@react-types/shared';
import classNames from 'classnames';
import type { HTMLAttributes, ReactNode, RefObject } from 'react';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useDOMRef } from '../../../utils';
import { Overlay } from '../controls/overlay';
import { Underlay } from '../controls/underlay';

interface TrayWrapperProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  isFixedHeight?: boolean;
  isNonModal?: boolean;
  overlayProps: HTMLAttributes<HTMLElement>;
}

function Tray(props: TrayProps, ref: DOMRef<HTMLDivElement>) {
  let { children, onClose, isFixedHeight, isNonModal, ...otherProps } = props;
  let domRef = useDOMRef(ref);

  let { overlayProps, underlayProps } = useOverlay({ ...props, isDismissable: true }, domRef);

  return (
    <Overlay {...otherProps}>
      <Underlay {...underlayProps} />
      <TrayWrapper
        className={props.UNSAFE_className}
        style={props.UNSAFE_style}
        onClose={onClose}
        ref={domRef}
        overlayProps={overlayProps}
        isFixedHeight={isFixedHeight}
        isNonModal={isNonModal}
      >
        {children}
      </TrayWrapper>
    </Overlay>
  );
}

let TrayWrapper = forwardRef((props: TrayWrapperProps, ref: RefObject<HTMLDivElement>) => {
  let { children, isOpen, isFixedHeight, isNonModal, overlayProps, ...otherProps } = props;
  usePreventScroll();
  let { modalProps } = useModal({
    isDisabled: isNonModal
  });

  let viewport = useViewportSize();
  let [height, setHeight] = useState(viewport.height);
  let timeoutRef = useRef<any>();

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (viewport.height < height && viewport.height < window.innerHeight) {
      timeoutRef.current = setTimeout(() => {
        setHeight(viewport.height);
      }, 500);
    } else {
      setHeight(viewport.height);
    }
  }, [height, viewport.height]);

  let wrapperStyle: any = {
    'visual-viewport-height-class': height + 'px'
  };

  let wrapperClassName = classNames('trayWrapperStyles');

  let className = classNames(
    'trayStyles',
    {
      'is-open-class': isOpen,
      'tray--fixedHeight-class': isFixedHeight
    },
    classNames('overrideStyles'),
    otherProps.className as any
  );

  let domProps = mergeProps(otherProps, overlayProps);

  return (
    <div className={wrapperClassName} style={wrapperStyle}>
      <div {...domProps} {...modalProps} className={className} ref={ref} data-testid="tray">
        {children}
      </div>
    </div>
  );
});

let _Tray = forwardRef(Tray);
_Tray.displayName = 'Tray';

export { _Tray as Tray };
